// The sentence analyser (वाक्यविश्लेषणम्): type or pick a Prakrit sentence, and every word gets its grammar and its
// likely role while the subject and the verb are checked for agreement. Runs entirely in the browser, debounced as
// the learner types. ?s= pre-fills the sentence, so any page can link to /grammar/vakya?s=…

import type { FC, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Info, Languages, ListOrdered } from 'lucide-react'
import { useKeyStatuses } from '@/hooks/use-statuses'
import { Bi } from '@/prakrit/components/common/bi'
import { Notice } from '@/prakrit/components/grammar/labs/lab-ui'
import { SentenceCard } from '@/prakrit/components/grammar/vakya/sentence-card'
import { StatTiles } from '@/prakrit/components/grammar/vakya/stat-tiles'
import { VakyaInput } from '@/prakrit/components/grammar/vakya/vakya-input'
import { WordCard } from '@/prakrit/components/grammar/vakya/word-card'
import { MAX_CHARS, MAX_WORDS, parseVakya } from '@/prakrit/components/grammar/vakya/vakya-parse'
import { VAKYA_EXAMPLES, exampleSentence } from '@/prakrit/components/grammar/vakya/vakya-examples'

const DEBOUNCE_MS = 250

export const VakyaAnalyser: FC = (): ReactNode => {
    const router = useRouter()
    const initial = useMemo(() => exampleSentence(VAKYA_EXAMPLES[0]), [])
    const [text, setText] = useState(initial)
    const [query, setQuery] = useState(initial)
    const inputRef = useRef<HTMLTextAreaElement>(null)
    const loaded = useRef(false)
    const touched = useRef(false)

    // ?s= pre-fills the sentence, read once the router knows the query.
    useEffect(() => {
        if (!router.isReady || loaded.current) return

        loaded.current = true

        const s = router.query.s

        if (typeof s !== 'string') return

        const value = s.normalize('NFC').slice(0, MAX_CHARS)

        if (!value.trim()) return

        setText(value)
        setQuery(value)
    }, [router.isReady, router.query.s])

    // Analyse a moment after the learner stops typing.
    useEffect(() => {
        if (text === query) return

        const id = window.setTimeout(() => setQuery(text), DEBOUNCE_MS)

        return () => window.clearTimeout(id)
    }, [text, query])

    // Keep ?s= in step with the analysed sentence once the learner has changed it, so the page can be shared.
    useEffect(() => {
        if (!loaded.current || !touched.current) return

        const value = query.normalize('NFC').trim()
        const current = typeof router.query.s === 'string' ? router.query.s.normalize('NFC').trim() : ''

        if (value === current) return

        const next = { ...router.query }

        if (value) next.s = value
        else delete next.s

        router.replace({ pathname: router.pathname, query: next }, undefined, { shallow: true, scroll: false }).catch(() => undefined)
    }, [query, router])

    const parse = useMemo(() => parseVakya(query), [query])
    const keys = useMemo(() => parse.words.flatMap(w => (w.chosen ? [w.chosen.key] : [])), [parse])
    const status = useKeyStatuses(keys)

    const change = (value: string) => {
        touched.current = true
        setText(value)
    }

    const pick = (sentence: string) => {
        touched.current = true
        setText(sentence)
        setQuery(sentence)
    }

    const clear = () => {
        touched.current = true
        setText('')
        setQuery('')
        inputRef.current?.focus()
    }

    return (
        <div className='flex flex-col gap-6'>
            <VakyaInput value={text} onChange={change} onPick={pick} onClear={clear} active={query} inputRef={inputRef} />

            {parse.words.length === 0 ? (
                <div className='rounded-3xl border border-dashed border-border bg-card/60 px-5 py-10 text-center text-sm text-muted-foreground'>
                    <Bi hi='ऊपर कोई प्राकृत वाक्य लिखिए, या एक उदाहरण चुनिए।' en='Type a Prakrit sentence above, or pick an example.' />
                </div>
            ) : (
                <>
                    <SentenceCard parse={parse} />
                    <StatTiles parse={parse} />

                    {(parse.latin || parse.truncated || parse.notes.length > 0) && (
                        <div className='flex flex-col gap-2'>
                            {parse.latin && (
                                <Notice tone='review' icon={<Languages className='size-4 text-saffron-strong' />}>
                                    <Bi
                                        hi='कुछ शब्द देवनागरी में नहीं हैं। प्राकृत देवनागरी लिपि में लिखिए।'
                                        en='Some words are not in Devanagari. Please type Prakrit in Devanagari script.'
                                    />
                                </Notice>
                            )}
                            {parse.truncated && (
                                <Notice icon={<ListOrdered className='size-4' />}>
                                    <Bi hi={`पहले ${MAX_WORDS} शब्दों का ही विश्लेषण किया गया है।`} en={`Only the first ${MAX_WORDS} words are analysed.`} />
                                </Notice>
                            )}
                            {parse.notes.map((note, i) => (
                                <Notice key={i} icon={<Info className='size-4' />}>
                                    <Bi hi={note.hi} en={note.en} />
                                </Notice>
                            ))}
                        </div>
                    )}

                    <section aria-labelledby='vakya-words'>
                        <h2 id='vakya-words' className='sr-only'>हर शब्द का विश्लेषण · Every word</h2>
                        <ol className='grid gap-4 md:grid-cols-2'>
                            {parse.words.map((w, i) => (
                                <li key={`${i}-${w.token}-${w.chosen?.key ?? 'unknown'}`} className='min-w-0'>
                                    <WordCard word={w} index={i} parse={parse} review={w.chosen ? status(w.chosen.key) : 'needs_review'} />
                                </li>
                            ))}
                        </ol>
                    </section>
                </>
            )}

            <p className='flex items-start gap-2 text-xs text-muted-foreground'>
                <Info className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                <Bi
                    hi='यह पूरा विश्लेषण व्याकरण के नियमों और रूप-तालिकाओं से आता है, अनुमान से कभी नहीं। शब्द पर टैप करके उसका पूरा विवरण देखिए।'
                    en='Everything here comes from the grammar rules and the form tables, never from guesswork. Tap a word for its full details.'
                />
            </p>
        </div>
    )
}
